import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, Plus } from 'lucide-react';
import { HiveShareVideo } from '@/services/hiveShareService';

interface VideoEditDialogProps {
  video: HiveShareVideo | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (videoId: string, updates: VideoUpdateData) => Promise<void>;
  families?: Array<{ id: string; name: string }>;
}

interface VideoUpdateData {
  title: string;
  description: string;
  privacy_level: 'organization' | 'selected_families';
  tags: string[];
  accessible_family_ids?: string[];
}

export const VideoEditDialog: React.FC<VideoEditDialogProps> = ({
  video,
  isOpen,
  onClose,
  onSave,
  families = []
}) => {
  const [formData, setFormData] = useState<VideoUpdateData>({
    title: video?.title || '',
    description: video?.description || '',
    privacy_level: video?.privacy_level || 'organization',
    tags: video?.tags || [],
    accessible_family_ids: video?.accessible_family_ids || []
  });
  
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Update form data when video changes
  React.useEffect(() => {
    if (video) {
      setFormData({
        title: video.title || '',
        description: video.description || '',
        privacy_level: video.privacy_level || 'organization',
        tags: video.tags || [],
        accessible_family_ids: video.accessible_family_ids || []
      });
    }
  }, [video]);

  const handleSave = async () => {
    if (!video) return;
    
    setIsLoading(true);
    try {
      await onSave(video.id, formData);
      onClose();
    } catch (error) {
      console.error('Failed to save video:', error);
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handlePrivacyLevelChange = (value: 'organization' | 'selected_families') => {
    setFormData(prev => ({ 
      ...prev, 
      privacy_level: value,
      // Clear accessible_family_ids when switching to organization
      accessible_family_ids: value === 'organization' ? [] : prev.accessible_family_ids
    }));
  };

  if (!video) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Video Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter video title"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter video description"
              rows={3}
            />
          </div>

          {/* Privacy Level */}
          <div className="space-y-2">
            <Label>Privacy Level</Label>
            <Select 
              value={formData.privacy_level} 
              onValueChange={handlePrivacyLevelChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="organization">Organization-wide</SelectItem>
                <SelectItem value="selected_families">Selected Families</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500">
              {formData.privacy_level === 'organization' 
                ? 'All families in the organization can view this video'
                : 'Only selected families can view this video'
              }
            </p>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a tag"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {tag}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                      onClick={() => handleRemoveTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Family Selection - Only show when privacy_level is 'selected_families' */}
          {formData.privacy_level === 'selected_families' && families.length > 0 && (
            <div className="space-y-2">
              <Label>Accessible Families</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {families.map((family) => (
                  <div key={family.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`family-${family.id}`}
                      checked={formData.accessible_family_ids?.includes(family.id) || false}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData(prev => ({
                            ...prev,
                            accessible_family_ids: [...(prev.accessible_family_ids || []), family.id]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            accessible_family_ids: prev.accessible_family_ids?.filter(id => id !== family.id) || []
                          }));
                        }
                      }}
                    />
                    <Label 
                      htmlFor={`family-${family.id}`} 
                      className="text-sm font-normal cursor-pointer"
                    >
                      {family.name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500">
                Select which families can access this video
              </p>
            </div>
          )}

          {/* Show message when no families available */}
          {formData.privacy_level === 'selected_families' && families.length === 0 && (
            <div className="space-y-2">
              <Label>Accessible Families</Label>
              <div className="p-4 border rounded-lg bg-gray-50">
                <p className="text-sm text-gray-600">
                  No families found in this organization. You may need to create families first.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoEditDialog;